let clientGeneratedMessageId = "a";

/**
 * Generate the next unique client side message id
 */
function nextElement() {
  // Step 1: Store the return value
  const returnVal = clientGeneratedMessageId;

  // Step 2: Store the last character
  let lastVal = clientGeneratedMessageId.charAt(
    clientGeneratedMessageId.length - 1
  );

  // Step 3: Remove the last character
  clientGeneratedMessageId = clientGeneratedMessageId.slice(
    0,
    clientGeneratedMessageId.length - 1
  );

  // Step 4: Increment last character
  if (lastVal === "z") {
    clientGeneratedMessageId += "aa";
  } else {
    lastVal = String.fromCharCode(lastVal.charCodeAt(0) + 1);
    clientGeneratedMessageId += lastVal;
  }

  // Step 5: Return the result
  return returnVal;
}

/**
 * Actions for chat
 */
export const actions = {
  /**
   * Send text message to friend
   *
   * @param {Object} context
   * @param {String} message
   */
  async sendMessage(context, { message, username, groupUsername }) {
    // Step 1: Get currently selected provider id
    const selectedProviderId = context.rootGetters.getUserDetails.id;

    // Step 2: Get selected user whose message we want
    const currentSelectedUserId = context.getters.getSelectedUserId;

    // Step 3: Get if is group
    const isGroup = context.getters.getIsGroup;

    if (selectedProviderId !== 0 && currentSelectedUserId !== 0) {
      // Start loading message
      if (isGroup) {
        context.commit("startSendingGroupMessage", currentSelectedUserId);
      } else {
        context.commit("startSendingMessage", currentSelectedUserId);
      }

      // Generate a message on the client side and add it
      //  to the array of message for quickly displaying a message and later editing the id
      const currentClientGeneratedMessageId = nextElement();
      const newMessageLocal = {
        receiver_id: currentSelectedUserId,
        sent_by: selectedProviderId,
        id: currentClientGeneratedMessageId.toString(),
        value: message,
        sent_time: Date.now(),
        read_time: null,
        type: 1,
        animate: false
      };

      // Getting the index at which the message is going to be inserted, so that later it can be edited with ease
      const messages = isGroup
        ? context.getters.getGroupMessages
        : context.getters.getMessages;
      let index = 0;
      if (currentSelectedUserId in messages) {
        index = messages[currentSelectedUserId].length;
      }

      // Adding message to list of message
      context.commit("addMessageFromClient", {
        message: newMessageLocal,
        id: currentSelectedUserId,
        isGroup
      });

      // Updating friend details
      context.commit("updateFriendDetails", {
        message: { ...newMessageLocal, fullName: username, online: null },
        id: currentSelectedUserId,
        isGroup,
        groupUsername
      });

      // Send the message
      const json = await context.dispatch(
        "makeAuthRequest",
        {
          url: "chat/message/send/text",
          method: "POST",
          chat: true,
          data: {
            message: message,
            receiverIds: [currentSelectedUserId],
            isGroup
          }
        },
        {
          root: true
        }
      );

      // Updating the added message
      if ("receiverInfoId" in json) {
        // Message has been successfully sent and editing the id
        context.commit("updateAddedMessage", {
          messageId: json.receiverInfoId,
          index: index,
          friendId: currentSelectedUserId,
          isGroup
        });
      }

      // Stop loading message
      if (isGroup) {
        context.commit("stopSendingGroupMessage", currentSelectedUserId);
      } else {
        context.commit("stopSendingMessage", currentSelectedUserId);
      }
    }
  },

  /**
   * Get the messages of the current selected user
   *
   * @param {Object} context
   */
  async getMessages(context, nextPage = false) {
    // Step 1: Get currently selected provider id
    const selectedProviderId = context.rootGetters.getUserDetails.id;

    // Step 2: Get selected user whose message we want
    const currentSelectedUserId = context.getters.getSelectedUserId;

    // Step 3: Get if is group
    const isGroup = context.getters.getIsGroup;

    // Step 4: Check if the user's message has been loaded
    let loadedMessagesOfUsers = context.getters.getLoadedMessagesOfUsers;
    if (isGroup) {
      loadedMessagesOfUsers = context.getters.getLoadedMessagesOfGroups;
    }

    if (currentSelectedUserId === 0 || selectedProviderId === 0) {
      return "NOT_LOADED";
    } else if (
      !(selectedProviderId in loadedMessagesOfUsers) ||
      loadedMessagesOfUsers[selectedProviderId].indexOf(
        currentSelectedUserId
      ) === -1 ||
      nextPage
    ) {
      // Start loading message
      if (isGroup) {
        context.commit("startLoadingGroupMessage", currentSelectedUserId);
      } else {
        context.commit("startLoadingMessage", currentSelectedUserId);
      }

      // Already loaded details
      let addLoaded = "addLoadedMessagesOfUsers";
      if (isGroup) {
        addLoaded = "addLoadedMessagesOfGroups";
      }
      context.commit(addLoaded, {
        userId: currentSelectedUserId,
        providerId: selectedProviderId
      });

      // Check if we have reached last page for this friend
      const lastPage =
        context.getters.getLastPage[selectedProviderId] &&
        context.getters.getLastPage[selectedProviderId][
          `${isGroup ? "group" : "user"}`
        ] &&
        context.getters.getLastPage[selectedProviderId][
          `${isGroup ? "group" : "user"}`
        ][currentSelectedUserId]
          ? context.getters.getLastPage[selectedProviderId][
              `${isGroup ? "group" : "user"}`
            ][currentSelectedUserId]
          : false;

      // Check if last page
      if (lastPage) {
        if (isGroup) {
          context.commit("stopLoadingGroupMessage", currentSelectedUserId);
        } else {
          context.commit("stopLoadingMessage", currentSelectedUserId);
        }
        return;
      }

      // Get current page that needs to be fetched
      const currentPage =
        context.getters.getPages[selectedProviderId] &&
        context.getters.getPages[selectedProviderId][
          `${isGroup ? "group" : "user"}`
        ] &&
        context.getters.getPages[selectedProviderId][
          `${isGroup ? "group" : "user"}`
        ][currentSelectedUserId] !== undefined
          ? context.getters.getPages[selectedProviderId][
              `${isGroup ? "group" : "user"}`
            ][currentSelectedUserId] + 1
          : 0;

      const inner = isGroup ? "group" : "user";
      const additionalOffset =
        inner in context.getters.getAdditionalOffset &&
        currentSelectedUserId in context.getters.getAdditionalOffset[inner]
          ? context.getters.getAdditionalOffset[inner][currentSelectedUserId]
          : 0;

      // Get all messages
      const json = await context.dispatch(
        "makeAuthRequest",
        {
          url: `chat/message/get/${currentSelectedUserId}/${currentPage}/${additionalOffset}${
            isGroup ? "?isGroup=true" : ""
          }`,
          method: "GET",
          chat: true
        },
        {
          root: true
        }
      );

      if ("messages" in json) {
        if (json.messages.length < 30) {
          // Last page reached
          context.commit("setLastPage", {
            userId: currentSelectedUserId,
            providerId: selectedProviderId
          });
        }
        if (json.messages.length > 0) {
          // Prepend to message
          json.messages.reverse();
          context.commit("prependMessages", {
            messages: json.messages,
            id: currentSelectedUserId,
            isGroup
          });
        }
      } else {
        // Remove from loaded list since there was an error
        context.commit("removeLoadedMessagesOfUsers", {
          userId: currentSelectedUserId,
          providerId: selectedProviderId
        });
      }

      // Increase page count
      context.commit("setPage", {
        providerId: selectedProviderId,
        userId: currentSelectedUserId,
        pageNumber: currentPage,
        isGroup
      });

      // Stop loading message
      if (isGroup) {
        context.commit("stopLoadingGroupMessage", currentSelectedUserId);
      } else {
        context.commit("stopLoadingMessage", currentSelectedUserId);
      }
    }
  },

  /**
   * Get the remaining messages between two users
   * That is messages after the last received message
   *
   * @param {Object} context
   */
  async getRemaingMessages(context) {
    // Step 1: Get currently selected provider id
    const selectedProviderId = context.rootGetters.getUserDetails.id;

    // Step 2: Get selected user whose message we want
    const currentSelectedUserId = context.getters.getSelectedUserId;

    // Step 3: Check if the user's message has been loaded
    const loadedMessagesOfUsers = context.getters.getLoadedMessagesOfUsers;

    // Step 5: Get if is group
    const isGroup = context.getters.getIsGroup;

    if (currentSelectedUserId === 0 || selectedProviderId === 0) {
      return "NOT_LOADED";
    } else if (
      !(
        selectedProviderId in loadedMessagesOfUsers &&
        loadedMessagesOfUsers[selectedProviderId].indexOf(
          currentSelectedUserId
        ) !== -1
      )
    ) {
      return "NO_MESSAGES";
    }

    try {
      // Step 5: Get the last message
      const lastId =
        context.getters.getMessages[currentSelectedUserId][
          context.getters.getMessages[currentSelectedUserId].length - 1
        ].id;

      // Step 6: Check if messasge id is valid
      if (isNaN(lastId) || parseInt(lastId) === 0) {
        return "WRONG_USER";
      }

      // Get all messages
      const json = await context.dispatch(
        "makeAuthRequest",
        {
          url: `chat/message/remaining/${currentSelectedUserId}/${lastId}`,
          method: "GET",
          chat: true
        },
        {
          root: true
        }
      );

      // Step 7: Append the new messages if any
      if ("messages" in json) {
        context.commit("appendRemainingMessages", {
          messages: json.messages,
          id: currentSelectedUserId,
          primaryUser: selectedProviderId,
          isGroup
        });
      }
    } catch (err) {
      return err;
    }
  },

  /**
   * Getting the unread messages of all the users
   *
   * @param {Object} context
   */
  async getDetails(context) {
    // Step 1: Get currently selected provider id
    const selectedProviderId = context.rootGetters.getUserDetails.id;

    if (context.getters.getFriends === false) {
      // Read the message
      const json = await context.dispatch(
        "makeAuthRequest",
        {
          url: "chat/message/details",
          method: "GET",
          chat: true
        },
        {
          root: true
        }
      );

      json.primaryId = selectedProviderId;
      context.commit("setFriends", json);
    }
  },
  /**
   * Read message
   * @param {Object} context
   */
  async readMessage(context) {
    // Step 1: Get selected user whose message we want
    const currentSelectedUserId = context.getters.getSelectedUserId;

    // Step 2: Get if is group
    const isGroup = context.getters.getIsGroup;

    // Step 3: Get the friends
    const friends = isGroup
      ? context.getters.getGroups
      : context.getters.getFriends;

    if (
      currentSelectedUserId &&
      currentSelectedUserId in friends &&
      friends[currentSelectedUserId].unread !== 0
    ) {
      // Remove all unread messages to get proper scroll behaviour
      context.commit("removeUnread", { id: currentSelectedUserId, isGroup });

      // Read the message
      await context.dispatch(
        "makeAuthRequest",
        {
          url: "chat/message/read",
          method: "PUT",
          chat: true,
          data: {
            id: currentSelectedUserId,
            isGroup
          }
        },
        {
          root: true
        }
      );
    }
  }
};
